import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SecurityIcon from "@mui/icons-material/Security";
import ForumIcon from "@mui/icons-material/Forum";
import GroupsIcon from "@mui/icons-material/Groups";
import TimerIcon from "@mui/icons-material/Timer";
import FlagIcon from "@mui/icons-material/Flag";

import PublicPageLayout from "../public-site/PublicPageLayout";
import { getPublicSiteSx } from "../public-site/publicSiteStyles";
import { useAuthState } from "../app/AuthStateContext";

const dimensions = [
  {
    name: "Sportsmanship",
    icon: EmojiEventsIcon,
    description: "Fair play, respectful behavior, and positive attitude towards teammates and opponents.",
    improve: "Avoid toxicity, respect others, and maintain a positive attitude even in tough matches.",
  },
  {
    name: "Integrity",
    icon: SecurityIcon,
    description: "Clean record without cheating, hacking, or exploiting game mechanics.",
    improve: "Play fair, avoid third-party tools, and maintain a clean gaming history.",
  },
  {
    name: "Communication",
    icon: ForumIcon,
    description: "Helpful, collaborative, and constructive communication with your team.",
    improve: "Share useful information, respond to callouts, and coordinate strategies.",
  },
  {
    name: "Team Play",
    icon: GroupsIcon,
    description: "Cooperative gameplay that prioritizes team objectives over individual performance.",
    improve: "Support teammates, play your role, and make decisions that benefit the team.",
  },
  {
    name: "Reliability",
    icon: TimerIcon,
    description: "Consistent match completion without abandoning or going AFK.",
    improve: "Finish matches you start, stay active during gameplay, and avoid disconnects.",
  },
  {
    name: "Reporter Quality",
    icon: FlagIcon,
    description: "Accurate and helpful reporting that improves the community.",
    improve: "Only report genuine violations, provide context, and avoid false accusations.",
  },
];

const trustTiers = [
  { label: "ELITE", range: "90-100", color: "#6FA86A" },
  { label: "HIGH", range: "75-89", color: "#9DB96F" },
  { label: "GOOD", range: "50-74", color: "#E8A33D" },
  { label: "FAIR", range: "30-49", color: "#B97A1F" },
  { label: "LOW", range: "0-29", color: "#D94F3D" },
];

const reputationChanges = [
  { marker: "+", title: "Match completion", body: "+0.5 to +2.0 points for finishing matches without issues" },
  { marker: "+", title: "Clean match streaks", body: "+5 bonus every 10 matches without reports or abandons" },
  { marker: "+", title: "Commendations", body: "+2 to +5 points when teammates recognize positive behavior" },
  { marker: "-", title: "Validated reports", body: "-5 to -20 points depending on offense severity" },
  { marker: "-", title: "Match abandonment", body: "-10 points for leaving matches early" },
  { marker: "-", title: "False reports you submit", body: "-5 to -20 Reporter Quality when reports are found incorrect" },
];

const reportSteps = [
  "Player submits a report with evidence and description",
  "Report enters admin review queue",
  "Admin reviews evidence, match data, and player histories",
  "Decision is made: validated, dismissed, or false report",
  "Reputation adjustments apply to both players based on the decision",
];

const penaltyRows = [
  {
    decision: "[ VALIDATED ]",
    decisionColor: "#6FA86A",
    reporter: "+2 Reporter Quality reward",
    reported: "-5 to -20 penalty by severity",
  },
  {
    decision: "[ DISMISSED ]",
    decisionColor: "#E8A33D",
    reporter: "-1 Reporter Quality minor adjustment",
    reported: "No penalty",
  },
  {
    decision: "[ FALSE REPORT ]",
    decisionColor: "#D94F3D",
    reporter: "-5 to -20 Reporter Quality",
    reported: "+2 compensation bonus",
  },
];

const privacyColumns = [
  {
    title: "Public signals",
    items: [
      "Trust tier",
      "Overall trust score",
      "Reputation breakdown",
      "Tier badge on your profile",
    ],
  },
  {
    title: "Private signals",
    items: [
      "Detailed reputation history",
      "Personalized improvement tips",
      "Comparison to platform averages",
    ],
  },
];

const faqItems = [
  {
    question: "Does my reputation decay over time?",
    answer:
      "No. Reputation is cumulative and does not decay. Scores change based on your actions, not the passage of time.",
  },
  {
    question: "Can I see other players' detailed reputation?",
    answer:
      "Ouro members can see other players' reputation breakdown when viewing their profile. Personal history and improvement tips stay private.",
  },
  {
    question: "How do I improve my reputation?",
    answer:
      "Complete matches, stay reliable, communicate constructively, and avoid reports. Your dashboard gives more specific tips based on your scores.",
  },
  {
    question: "What if I am falsely reported?",
    answer:
      "Reports go through admin review before affecting reputation. False reports can penalize the reporter and compensate the reported player.",
  },
  {
    question: "Can I reset my reputation?",
    answer:
      "No. Reputation is permanent and tied to your account so accountability cannot be escaped by starting over.",
  },
  {
    question: "How are the six dimensions weighted?",
    answer:
      "Integrity, Sportsmanship, and Reliability carry the most weight because they have the biggest impact on match quality.",
  },
];

function ReputationExplainerPage() {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);
  const { isAuthenticated } = useAuthState();

  return (
    <PublicPageLayout
      eyebrow="Reputation"
      title="Trust you can read before you queue."
      description="Ouro reputation gives founding players a reason to trust the people around them, with long-term signals for conduct, reliability, communication, and report quality."
      mapBackground
    >
      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Reputation is the long game.</Typography>
          <Typography sx={sx.sectionCopy}>
            Your reputation is a lifetime cumulative record of behavior and
            contribution on Ouro. Every player starts with a score of 50/100 in
            each dimension, and the score changes as the platform sees how you
            play, communicate, finish matches, and use reports.
          </Typography>
        </Box>

        <Box sx={{ ...sx.cardGrid, gridTemplateColumns: { xs: "1fr", lg: "1.08fr 0.92fr" } }}>
          <Box sx={sx.card}>
            <Typography sx={sx.cardTitle}>Trust tiers</Typography>
            <Typography sx={{ ...sx.cardBody, mb: 2 }}>
              Tiers keep the signal readable without hiding the underlying
              score range.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {trustTiers.map((tier) => (
                <Box
                  key={tier.label}
                  sx={{
                    px: 1.5,
                    py: 0.9,
                    borderRadius: "4px",
                    border: `1px solid ${tier.color}`,
                    backgroundColor: "rgba(255,255,255,0.025)",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  <Typography component="span" sx={{ fontSize: "0.72rem", fontWeight: 700, color: tier.color }}>
                    [ {tier.label} ]
                  </Typography>
                  <Typography component="span" sx={{ ml: 0.75, fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }}>
                    {tier.range}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={sx.accentPanel}>
            <Typography sx={sx.cardTitle}>Reputation vs. Impact Rating</Typography>
            <Typography sx={sx.cardBody}>
              Reputation tracks how you play over time: reliability,
              sportsmanship, communication, and conduct. Impact Rating tracks
              what you did in a specific match: opening duels, trades, utility,
              and playing for the round.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Six signals, one trust picture.</Typography>
          <Typography sx={sx.sectionCopy}>
            Reputation is split into independent dimensions so a player can be
            reliable in one area and still have clear room to improve in another.
          </Typography>
        </Box>

        <Box sx={{ ...sx.cardGrid, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" } }}>
          {dimensions.map((dim) => {
            const Icon = dim.icon;
            return (
              <Box key={dim.name} sx={sx.card}>
                <Box sx={sx.cardIconWrap}>
                  <Icon />
                </Box>
                <Typography sx={sx.cardTitle}>{dim.name}</Typography>
                <Typography sx={sx.cardBody}>{dim.description}</Typography>
                <Typography sx={{ ...sx.cardBody, mt: 1.5, color: "#E8A33D", fontWeight: 700 }}>
                  Improve: {dim.improve}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={sx.sectionWithMapBg1}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Actions move the score.</Typography>
          <Typography sx={sx.sectionCopy}>
            Reputation shifts through both positive and negative signals. The
            goal is not to punish a single rough match; it is to make long-term
            patterns visible.
          </Typography>
        </Box>

        <Box sx={sx.card}>
          <Box component="ul" sx={{ ...sx.bulletList, listStyle: "none", pl: 0 }}>
            {reputationChanges.map((item) => (
              <li key={item.title}>
                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 1.5, alignItems: "start" }}>
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "0.9rem",
                      fontWeight: 800,
                      whiteSpace: "nowrap",
                      color: item.marker === "+" ? "#6FA86A" : "#D94F3D",
                    }}
                  >
                    [ {item.marker} ]
                  </Typography>
                  <Typography component="span" sx={sx.listItemText}>
                    <Box component="strong" sx={{ color: "#ffffff" }}>{item.title}:</Box>{" "}
                    {item.body}
                  </Typography>
                </Box>
              </li>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Reports are accountable both ways.</Typography>
          <Typography sx={sx.sectionCopy}>
            Reports affect the reported player and the reporter. This keeps
            reporting useful while protecting honest players from noise and abuse.
          </Typography>
        </Box>

        <Box sx={{ ...sx.cardGrid, gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.1fr" } }}>
          <Box sx={sx.card}>
            <Typography sx={sx.cardTitle}>Report review process</Typography>
            <Box component="ol" sx={{ ...sx.bulletList, pl: 0, listStyle: "none" }}>
              {reportSteps.map((step, index) => (
                <li key={step}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "34px 1fr", gap: 1.5, alignItems: "start" }}>
                    <Typography component="span" sx={sx.stepNumber}>
                      {index + 1}
                    </Typography>
                    <Typography component="span" sx={sx.listItemText}>{step}</Typography>
                  </Box>
                </li>
              ))}
            </Box>
          </Box>

          <Box sx={sx.tableWrap}>
            <Box component="table" sx={sx.comparisonTable}>
              <thead>
                <tr>
                  <th><Typography component="span" sx={sx.tableHeadText}>Decision</Typography></th>
                  <th><Typography component="span" sx={sx.tableHeadText}>Reporter</Typography></th>
                  <th><Typography component="span" sx={sx.tableHeadText}>Reported player</Typography></th>
                </tr>
              </thead>
              <tbody>
                {penaltyRows.map((row) => (
                  <tr key={row.decision}>
                    <td>
                      <Typography component="span" sx={{ ...sx.tableCellText, fontFamily: "'JetBrains Mono', monospace", color: row.decisionColor, fontWeight: 700 }}>
                        {row.decision}
                      </Typography>
                    </td>
                    <td><Typography component="span" sx={sx.tableCellText}>{row.reporter}</Typography></td>
                    <td><Typography component="span" sx={sx.tableCellText}>{row.reported}</Typography></td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Visible where it helps, private where it should be.</Typography>
        </Box>

        <Box sx={sx.cardGrid}>
          {privacyColumns.map((column) => (
            <Box key={column.title} sx={sx.card}>
              <Typography sx={sx.cardTitle}>{column.title}</Typography>
              <Box component="ul" sx={sx.bulletList}>
                {column.items.map((item) => (
                  <li key={item}>
                    <Typography component="span" sx={sx.listItemText}>
                      {item}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Reputation FAQ.</Typography>
        </Box>

        <Box sx={sx.faqList}>
          {faqItems.map((item) => (
            <Box key={item.question} sx={sx.faqCard}>
              <Typography sx={sx.faqQuestion}>{item.question}</Typography>
              <Typography sx={sx.faqAnswer}>{item.answer}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={sx.callout}>
        <Typography sx={sx.sectionCopy}>
          Reputation becomes most useful when players can see it before they
          commit to a queue.
        </Typography>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Button
            variant="contained"
            component={Link}
            to={isAuthenticated ? "/account/reputation" : "/#get-started"}
            sx={sx.primaryButton}
          >
            {isAuthenticated ? "View your reputation dashboard" : "Join the founding community"}
          </Button>
          <Button component={Link} to="/trust-and-safety" variant="outlined" sx={sx.secondaryButton}>
            See trust and safety
          </Button>
        </Box>
      </Box>
    </PublicPageLayout>
  );
}

export default ReputationExplainerPage;
